/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/class-methods-use-this -- Methods are part of service interface */
/* eslint-disable @typescript-eslint/prefer-readonly -- Config is assigned in constructor */
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- Type guards used before assertion */
/**
 * Minimal LibP2P Service for Browser
 *
 * Stripped down to essentials:
 * - WebSocket transport only (browsers can't use TCP)
 * - PSK (pre-shared key) for private network
 * - GossipSub pubsub
 * - Bootstrap to HAP
 * - Message counting
 */

import { noise } from '@chainsafe/libp2p-noise';
import { yamux } from '@chainsafe/libp2p-yamux';
import { identify } from '@libp2p/identify';
import { webSockets } from '@libp2p/websockets';
import { bootstrap } from '@libp2p/bootstrap';
import { createLibp2p, type Libp2p } from 'libp2p';
import { fromString as uint8ArrayFromString } from 'uint8arrays/from-string';
import { floodsub } from '@libp2p/floodsub'
import type { Identify } from '@libp2p/identify';
import type { PeerDiscovery } from '@libp2p/interface';
import { ping } from '@libp2p/ping';

import { P2P_CONFIG } from '@/config/p2pConfig';
import { createLogger } from '@/utils/logger';

const log = createLogger({ namespace: P2P_CONFIG.logging.namespace.libp2p });

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export type MinimalLibp2p = Libp2p<{
  identify: Identify;
  pubsub: any;
  bootstrap: PeerDiscovery;
}>;

export interface MinimalNodeConfig {
  bootstrapAddresses: string[];
  swarmKey: string;
}

export interface MessageCounts {
  sent: number;
  received: number;
}

/* -------------------------------------------------------------------------- */
/*  Implementation                                                             */
/* -------------------------------------------------------------------------- */

export class MinimalLibp2pService {
  private messageSentCount = 0;
  private messageReceivedCount = 0;
  private config: MinimalNodeConfig;

  constructor(config?: Partial<MinimalNodeConfig>) {
    this.config = {
      bootstrapAddresses: config?.bootstrapAddresses ?? P2P_CONFIG.network.defaultBootstrap,
      swarmKey: config?.swarmKey ?? P2P_CONFIG.network.defaultSwarmKey
    };
  }

  /**
   * Format swarm key to PSK format expected by libp2p.
   */
  private formatSwarmKeyToPsk(swarmKeyHex: string): Uint8Array {
    const pskString = `/key/swarm/psk/1.0.0/
/base16/
${swarmKeyHex}`;
    return uint8ArrayFromString(pskString);
  }

  /**
   * Create a minimal libp2p node.
   */
  async createNode(): Promise<MinimalLibp2p> {
    const PEER_ID_LOG_LENGTH = P2P_CONFIG.logging.peerIdLogLength;
    log.info('Creating browser node');
    log.info('Bootstrap', { address: this.config.bootstrapAddresses[0] });

    const node = await createLibp2p({
      addresses: {
        listen: []
      },
      transports: [
        webSockets()
      ],
      connectionManager: {
        maxConnections: P2P_CONFIG.network.maxConnections,
        autoDial: P2P_CONFIG.network.autoDial, // Retry connection if it drops
      } as any,
      connectionEncrypters: [noise()],
// ... inside createLibp2p ...

      streamMuxers: [yamux({
        // CRITICAL FIX: Reduce Max Streams from 1000 to 32
        // This prevents Bitswap from opening 60+ parallel streams and choking the connection.
        maxInboundStreams: P2P_CONFIG.yamux.maxInboundStreams,
        maxOutboundStreams: P2P_CONFIG.yamux.maxOutboundStreams,

        enableKeepAlive: P2P_CONFIG.yamux.enableKeepAlive,
        keepAliveInterval: P2P_CONFIG.yamux.keepAliveInterval,

        // ... keep your window size config from before ...
        clientStreamWindowSize: P2P_CONFIG.yamux.clientStreamWindowSize,
        maxStreamWindowSize: P2P_CONFIG.yamux.maxStreamWindowSize,
        streamOptions: {
           initialStreamWindowSize: P2P_CONFIG.yamux.initialStreamWindowSize,
           maxStreamWindowSize: P2P_CONFIG.yamux.maxStreamWindowSize,
           maxInboundStreams: P2P_CONFIG.yamux.maxInboundStreams,
           maxOutboundStreams: P2P_CONFIG.yamux.maxOutboundStreams,
        }
      } as any)],
        services: {
        ping: ping({
           timeout: P2P_CONFIG.ping.timeout
        }),
        identify: identify(),
        pubsub: floodsub(),
        bootstrap: bootstrap({
          list: this.config.bootstrapAddresses
        })
      }
    });

    await node.start();

    const peerId = node.peerId.toString();
    log.info('Node started', { peerId });
    log.info('Listening addresses', { addresses: node.getMultiaddrs().map(ma => ma.toString()) });

    // Peer discovery events
    node.addEventListener('peer:discovery', (evt) => {
      const discoveredPeer = evt.detail;
      log.debug('Peer discovered', {
        peerId: discoveredPeer.id.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        multiaddrs: discoveredPeer.multiaddrs.map(ma => ma.toString())
      });
    });

    // Peer connection events
    node.addEventListener('peer:connect', (evt) => {
      const remotePeer = evt.detail;
      const connections = node.getConnections(remotePeer);
      log.info('Peer connected', {
        peerId: remotePeer.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        totalConnections: node.getConnections().length,
        connectionsToThisPeer: connections.length,
        connectionDetails: connections.map(conn => ({
          id: conn.id,
          remoteAddr: conn.remoteAddr.toString(),
          direction: conn.direction,
          status: conn.status,
          timeline: conn.timeline
        }))
      });
    });

    // Peer disconnection events
    node.addEventListener('peer:disconnect', (evt) => {
      const remotePeer = evt.detail;
      log.info('Peer disconnected', {
        peerId: remotePeer.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        remainingConnections: node.getConnections().length
      });
    });

    // Connection open events (more detailed than peer:connect)
    node.addEventListener('connection:open', (evt) => {
      const connection = evt.detail;
      log.debug('Connection opened', {
        connectionId: connection.id,
        remotePeer: connection.remotePeer.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        remoteAddr: connection.remoteAddr.toString(),
        direction: connection.direction,
        encryption: connection.encryption,
        multiplexer: connection.multiplexer,
        status: connection.status,
        timeline: connection.timeline
      });
    });

    // Connection close events
    node.addEventListener('connection:close', (evt) => {
      const connection = evt.detail;
      log.debug('Connection closed', {
        connectionId: connection.id,
        remotePeer: connection.remotePeer.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        remoteAddr: connection.remoteAddr.toString(),
        direction: connection.direction,
        timeline: connection.timeline
      });
    });

    // Self connection events
    node.addEventListener('self:peer:update', (evt) => {
      log.debug('Self peer updated', {
        peerId: evt.detail.peer.id.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        multiaddrs: evt.detail.peer.addresses.map(a => a.multiaddr.toString())
      });
    });

    // Transport listener events
    node.addEventListener('transport:listening', (evt) => {
      log.debug('Transport listening', { detail: evt.detail });
    });

    node.addEventListener('transport:close', (evt) => {
      log.debug('Transport closed', { detail: evt.detail });
    });

    // Pubsub events
    const pubsub = node.services.pubsub;

    pubsub.addEventListener('subscription-change', (evt) => {
      log.debug('Subscription change', {
        peerId: evt.detail.peerId.toString().substring(0, PEER_ID_LOG_LENGTH) + '...',
        subscriptions: evt.detail.subscriptions.map(s => ({
          topic: s.topic,
          subscribe: s.subscribe
        }))
      });
    });

    // Start event (node fully started)
    node.addEventListener('start', () => {
      log.info('Node fully started');
    });

    // Stop event
    node.addEventListener('stop', () => {
      log.info('Node stopped');
    });

    return node;
  }

  /**
   * Stop the node.
   */
  async stopNode(node: MinimalLibp2p): Promise<void> {
    log.info('Stopping node');
    await node.stop();
    log.info('Node stopped');
  }

  /**
   * Subscribe to a topic.
   */
  subscribeToTopic(
    node: MinimalLibp2p,
    topic: string,
    handler: (message: Uint8Array, from: string) => void
  ): () => void {
    log.debug('Subscribing to topic', { topic });

    const pubsub = node.services.pubsub;
    pubsub.subscribe(topic);

    const messageHandler = (evt: CustomEvent): void => {
      const detail: unknown = evt.detail;
      if (
        typeof detail === 'object' &&
        detail !== null &&
        'topic' in detail &&
        'data' in detail &&
        'from' in detail &&
        (detail as { topic: unknown }).topic === topic &&
        (detail as { data: unknown }).data instanceof Uint8Array &&
        typeof (detail as { from: unknown }).from === 'object' &&
        (detail as { from: unknown }).from !== null &&
        'toString' in ((detail as { from: object }).from)
      ) {
        const typedDetail = detail as { topic: string; data: Uint8Array; from: { toString: () => string } };
        this.messageReceivedCount++;
        handler(typedDetail.data, typedDetail.from.toString());
      }
    };

    pubsub.addEventListener('message', messageHandler as EventListener);

    return () => {
      log.debug('Unsubscribing from topic', { topic });
      pubsub.removeEventListener('message', messageHandler as EventListener);
      pubsub.unsubscribe(topic);
    };
  }

  /**
   * Publish to a topic.
   */
  async publishToTopic(
    node: MinimalLibp2p,
    topic: string,
    data: Uint8Array | string
  ): Promise<void> {
    const messageData = typeof data === 'string'
      ? new TextEncoder().encode(data)
      : data;

    const pubsub = node.services.pubsub;
    const subscribers = pubsub.getSubscribers(topic);

    log.debug('Publishing to topic', { topic, subscriberCount: subscribers.length });

    await pubsub.publish(topic, messageData);
    this.messageSentCount++;
  }

  /**
   * Get message counts.
   */
  getMessageCounts(): MessageCounts {
    return {
      sent: this.messageSentCount,
      received: this.messageReceivedCount
    };
  }

  /**
   * Reset message counts.
   */
  resetMessageCounts(): void {
    this.messageSentCount = 0;
    this.messageReceivedCount = 0;
  }

  /**
   * Get connection info.
   */
  getConnectionInfo(node: MinimalLibp2p): {
    peerId: string;
    connections: number;
    subscribers: number;
  } {
    return {
      peerId: node.peerId.toString(),
      connections: node.getConnections().length,
      subscribers: node.services.pubsub.getSubscribers('test-pubsub').length
    };
  }
}

// Export singleton for convenience
export const minimalLibp2pService = new MinimalLibp2pService();
